import { useCallback, useRef } from "react";
import type { TouchEventHandler } from "react";

type UseSwipeTabsParams = {
    tabIndex: number;
    tabCount: number;
    onSwipe: (newIndex: number) => void;
    enabled: boolean;
};

type UseSwipeTabsResult = {
    onTouchStart: TouchEventHandler;
    onTouchEnd: TouchEventHandler;
};

export const useSwipeTabs = ({
    tabIndex,
    tabCount,
    onSwipe,
    enabled,
}: UseSwipeTabsParams): UseSwipeTabsResult => {
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const swiping = useRef(false);

    const onTouchStart: TouchEventHandler = useCallback(
        (e) => {
            if (!enabled) return;
            touchStartX.current = e.touches[0].clientX;
            touchStartY.current = e.touches[0].clientY;
            swiping.current = true;
        },
        [enabled],
    );

    const onTouchEnd: TouchEventHandler = useCallback(
        (e) => {
            if (!enabled || !swiping.current) return;
            swiping.current = false;

            const deltaX = e.changedTouches[0].clientX - touchStartX.current;
            const deltaY = e.changedTouches[0].clientY - touchStartY.current;

            if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY)) return;

            if (deltaX < 0 && tabIndex < tabCount - 1) {
                onSwipe(tabIndex + 1);
            } else if (deltaX > 0 && tabIndex > 0) {
                onSwipe(tabIndex - 1);
            }
        },
        [enabled, tabIndex, tabCount, onSwipe],
    );

    return { onTouchStart, onTouchEnd };
};
